import { orderBy } from 'lodash-es'

import {
  stringifyQueryString,
  useConvenientInfiniteQuery,
  useQuery,
} from '@fv/client-core'
import type {
  NotificationFilter,
  UserNotification,
  UserNotificationSettings,
} from '@fv/models'
import type { DTO } from '@fv/models/core'

import { shipperFetch } from '@/utils/shipperFetch'

export type Notification = DTO<UserNotification>

export const defaultCountFilter: NotificationFilter = {
  onlyUnread: true,
}

const rootKey = ['notifications']
export const notificationKeys = {
  all: [...rootKey],
  userSettings: [...rootKey, 'user-notification-settings'],
  counts: [...rootKey, 'notification-counts'],
  list: () => [...rootKey, 'list'],
  defaultCounts: () => [...notificationKeys.counts, 'default'],
  filteredCountBase: () => [...notificationKeys.counts, 'filter'],
  byId: (id: string) => [...notificationKeys.all, 'id', id],
  filter: (filter: NotificationFilter) => [...notificationKeys.list(), filter],
  filteredCount: (filter: NotificationFilter) =>
    Object.keys(filter).length > 0
      ? [...notificationKeys.filteredCountBase(), filter]
      : notificationKeys.defaultCounts(),
}

export const useNotification = (id: string) => {
  return useQuery({
    queryKey: [...notificationKeys.byId(id)],
    queryFn: () => shipperFetch<Notification>(`/notifications/${id}`),
    enabled: !!id,
  })
}

const fetchNotifications = async (
  filter: NotificationFilter,
): Promise<Notification[]> => {
  return await shipperFetch(`/notifications?${stringifyQueryString(filter)}`)
}

export const useNotifications = (filter: NotificationFilter) => {
  return useConvenientInfiniteQuery({
    queryKey: [...notificationKeys.filter(filter)],
    queryFn: ({ continuationToken, limit }) =>
      fetchNotifications({
        ...filter,
        continuationToken,
        limit,
      }),
    limit: 50,
    select: data =>
      orderBy(data, [p => p.pinned === true, 'created'], ['desc', 'desc']), // checking pin === true due to cache modifiers causing pinned=false, pinned=undefined, pinned=null
  })
}

const fetchCount = async (filter: NotificationFilter): Promise<number> => {
  return await shipperFetch<number>(
    `/notifications/count?${stringifyQueryString(filter)}`,
  )
}

export const useNotificationCount = (filter: NotificationFilter) => {
  return useQuery({
    queryKey: [...notificationKeys.filteredCount(filter)],
    queryFn: () => fetchCount(filter),
  })
}

const fetchUserNotificationSettings =
  async (): Promise<UserNotificationSettings> =>
    await shipperFetch('/notifications/user')

export const useUserNotificationSettings = () => {
  return useQuery({
    queryKey: notificationKeys.userSettings,
    queryFn: fetchUserNotificationSettings,
  })
}
